import moment from 'moment';

class DateService {
	constructor() {
		// Current time and default values for the daterange picker.
		this.dateTime = new Date();

		// Default to month to date
		this.defaultEndDate = this.getDateTime();
		this.defaultStartDate = this.getFirstOfMonth();
		this.currentMonth = this.getMonthFromDateTime(this.dateTime);
	}

	getPresetDateRanges() {
		const today = this.getCurrentDateTime();
		const yesterday = this.offsetFromDate(today, -1, 'day');
		const firstOfMonth = this.getFirstOfMonth();

		return {
			today: {
				label: 'Today',
				startDate: today,
				endDate: today,
			},
			yesterday: {
				label: 'Yesterday',
				startDate: yesterday,
				endDate: yesterday,
			},
			thisWeek: {
				label: 'This Week',
				startDate: this.getFirstOfWeek(),
				endDate: today,
			},
			oneDay: {
				label: '24 hours',
				startDate: this.offsetFromDate(today, -1, 'day'),
				endDate: today,
			},
			sevenDay: {
				label: 'Last 7 Days',
				startDate: this.offsetFromDate(today, -6, 'day'),
				endDate: today,
			},
			thirtyDay: {
				label: 'Last 30 Days',
				startDate: this.offsetFromDate(today, -29, 'day'),
				endDate: today,
			},
			ninetyDay: {
				label: 'Last 90 Days',
				startDate: this.offsetFromDate(today, -89, 'day'),
				endDate: today,
			},
			thisMonth: {
				label: 'This Month',
				startDate: firstOfMonth,
				endDate: today,
				selected: true,
			},
			lastMonth: {
				label: 'Last Month',
				startDate: this.offsetFromDate(firstOfMonth, -1, 'month'),
				endDate: this.offsetFromDate(firstOfMonth, -1, 'day'),
			},
			last2Months: {
				label: 'Last 2 Months',
				startDate: this.offsetFromDate(firstOfMonth, -1, 'month'),
				endDate: today,
			},
			halfYear: {
				label: 'Last 6 Months',
				startDate: this.offsetFromDate(firstOfMonth, -5, 'month'),
				endDate: today,
			},
			thisYear: {
				label: 'Year to Date',
				startDate: this.getStartOf(today, 'year'),
				endDate: today,
			},
			multiYear: {
				label: 'Year over Year',
				startDate: moment(new Date(2016, 0, 1)),
				endDate: today,
			},
		};
	}

	getCurrentDateTime() {
		return moment();
	}

	getDateTime() {
		return moment(this.dateTime.toISOString());
	}

	getStartOfDay(date) {
		date = date ? date.clone() : this.getDateTime();
		return date.startOf('day');
	}

	getEndOfDay(date) {
		date = date ? date.clone() : this.getDateTime();
		return date.endOf('day');
	}

	getFirstOfWeek() {
		return this.getDateTime().startOf('week');
	}

	getEndOfWeek() {
		return this.getDateTime().endOf('week').startOf('day');
	}

	getFirstOfMonth(date) {
		date = date ? date.clone() : this.getDateTime();
		return date.startOf('month');
	}

	getLastMonthStart() {
		const lastMonthDate = this.offsetFromDate(this.getDateTime(), -1, 'month');
		return this.getFirstOfMonth(lastMonthDate);
	}

	getEndOfMonth(date) {
		date = date ? date.clone() : this.getDateTime();
		return date.endOf('month');
	}

	getStartOf(date, unit) {
		// unit: day|week|month|year
		date = date ? date.clone() : this.getDateTime();
		return date.startOf(unit);
	}

	getMonthLabel(date) {
		// Ex: JAN, FEB, SEP
		return this.getMonthFromDateTime(date, 'MMM').toUpperCase();
	}

	toIso8601UtcDatetime(dateObj) {
		return moment(dateObj).utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]');
	}

	formatDate(dateObj, fmt, isUtc) {
		fmt = fmt || 'YYYY-MM-DD';
		const date = moment(dateObj);
		return isUtc ? date.utc().format(fmt) : date.format(fmt);
	}

	formatDisplayDate(dateObj) {
		return moment(dateObj).format('MMMM D, YYYY');
	}

	formatMonthDay(dateObj) {
		return moment(dateObj).format('M/D/YY');
	}

	formatDisplayMonth(dateObj) {
		return moment(dateObj).format('MMMM');
	}

	formatYear(dateObj) {
		return moment(dateObj).format('YYYY');
	}

	formatYearMonth(dateObj) {
		return moment(dateObj).format('YYYY-MM');
	}

	formatTime(dateObj) {
		return moment(dateObj).format('h:mm a');
	}

	localizeDate(dateStr) {
		// Make sure date is localized if dateStr is a year-month format instead of utc
		if (!dateStr.match(/Z$/)) {
			const date = new Date(dateStr);
			return date.getTime() + date.getTimezoneOffset() * 60000;
		} else {
			return moment.utc(dateStr).toDate().getTime();
		}
	}

	dateFromStr(dateStr) {
		return dateStr.match(/Z$/) ? moment.utc(dateStr) : moment(dateStr);
	}

	formatDateRange(dateObj) {
		const start = moment(dateObj.startDate).format('YYYY-MM-DD');
		const end = moment(dateObj.endDate).format('YYYY-MM-DD');
		return {startDate: start, endDate: end};
	}

	subtractDates(d1, d2, unit='') {
		// Returns a float or momentObj
		const momentObj = moment.duration(Math.abs(moment(d1) - moment(d2)));

		if (unit === 'hour') {
			return momentObj.asHours() + 1;
		} else if (unit === 'day') {
			return momentObj.asDays() + 1;
		} else if (unit === 'month') {
			return momentObj.asMonths() + 1;
		} else if (unit === 'year') {
			return momentObj.asYears() + 1;
		} else {
			return momentObj;
		}
	}

	offsetFromDate(originalDate, number, unit) {
		// number can be positive or negative
		unit = unit || 'day';
		return moment(originalDate).add(number, unit);
	}

	// Sort dates in chronological order
	compareDates(d1, d2) {
		if (moment(d1).isSame(d2, 'day')) return 0;
		else if (moment(d1).isBefore(d2, 'day')) return -1;
		return 1;
	}

	compareDateWithToday(d1) {
		return this.compareDates(d1, this.localizeDate(new Date().toISOString()));
	}

	getMonthFromDateTime(dateObj, monthFormat=null) {
		monthFormat = monthFormat || 'MMMM';
		return moment(dateObj).format(monthFormat);
	}

	getDaysInMonth(dateObj) {
		return moment(dateObj).daysInMonth();
	}

	dayElapsed(forProjection) {
		let dayElapsed;
		if (forProjection) {
			// Project using 'seconds'
			const today = this.getCurrentDateTime();
			const start = today.clone().startOf('day');
			const timeElapsed = moment.duration(today.diff(start));
			const secondsElapsed = timeElapsed.asSeconds();
			const totalSecondsInDay = 86400;
			dayElapsed = secondsElapsed / totalSecondsInDay;
		} else {
			const today = moment().utc();
			const hour = today.format('HH');
			const totalHours = 24;

			dayElapsed = hour / totalHours;
		}
		return dayElapsed;
	}

	monthElapsed(forProjection) {
		let monthElapsed;
		if (forProjection) {
			// Project using 'hours'
			const today = this.getCurrentDateTime();
			const start = today.clone().startOf('month');
			const timeElapsed = moment.duration(today.diff(start));
			const hoursElapsed = timeElapsed.asHours();
			monthElapsed = hoursElapsed / (today.daysInMonth() * 24);
		} else {
			const today = moment().utc();
			const date = today.date();
			const daysInMonth = today.daysInMonth();

			monthElapsed = date / daysInMonth;
		}
		return monthElapsed;
	}

	yearElapsed(forProjection) {

		let yearElapsed;
		if (forProjection) {
			// Project using 'days'
			const today = this.getCurrentDateTime();
			const start = today.clone().startOf('year');
			const timeElapsed = moment.duration(today.diff(start));
			const daysElapsed = timeElapsed.asDays();
			yearElapsed = daysElapsed / this.getDaysInYear();
		} else {
			const today = moment().utc();
			const daysElapsed = today.dayOfYear();

			yearElapsed = daysElapsed / this.getDaysInYear();
		}
		return yearElapsed;
	}

	getDaysInYear() {
		const today = this.getCurrentDateTime().utc();
		const year = today.year();
		// Find if current year is leap year
		return year % 100 === 0
			? year % 400 === 0
				? 366
				: 365
			: year % 4 === 0
				? 366
				: 365;
	}

	timeFrom(date, measurement, floating) {
		let original = date;
		const now = moment();
		if (date instanceof Date) {
			original = moment(date);
		} else if (typeof date === 'string') {
			original = this.dateFromStr(date);
		}

		return original.diff(now, measurement, floating);
	}
}

export const dateService = new DateService();
